import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../../constants';
import { useDebouncedValue } from '../../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../../schema/hannibal/HannibalContext';
import {
  PractitionerSuggestionsDocument,
  SuggestionPractitionerFragment,
} from '../../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../../util/EMPTY_LIST';
import { SuggestionData } from '../../types';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import {
  CategoryView,
  CategoryLabel,
  ListContainer,
} from '../SuggestionsLayoutTemplate/SuggestionsLayoutTemplate.styles';
import { TabProps } from '../TabProps';

export const mapPractitionerSuggestionsToSuggestionData = (
  profiles: readonly SuggestionPractitionerFragment[],
): SuggestionData[] => [
  ...profiles
    .filter((it): it is SuggestionPractitionerFragment => it.__typename === 'Practitioner')
    ?.map(
      (it): SuggestionData => ({
        type: 'PractitionerSuggestion',
        id: `practitioner-${it.id}`,
        practitioner: {
          id: it.id,
          title: it.title ?? undefined,
          firstName: it.firstName ?? undefined,
          lastName: it.lastName ?? undefined,
          specialties: it.specialties?.map(specialty => specialty.name) ?? [],
          suburb: it.primaryPractice?.address?.suburb ?? undefined,
          state: it.primaryPractice?.address?.state ?? undefined,
          imagePath: it.profilePhoto?.link ?? undefined,
          url: it.url,
        },
      }),
    ),
];

export const PractitionerSuggestions = ({
  searchTerm,
  onChoose,
  trackingData,
  toggleSingleSuggestionView,
  showSingleView,
  onFocus,
  listIndex = 0,
}: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(PractitionerSuggestionsDocument, { variables: { query } });

  const profiles = data?.searchPractitionersByName.practitioners ?? EMPTY_LIST;

  const practitionerSuggestions: SuggestionData[] = useMemo(() => {
    if (showSingleView) {
      return mapPractitionerSuggestionsToSuggestionData(profiles);
    }

    return mapPractitionerSuggestionsToSuggestionData(profiles).slice(0, 3);
  }, [showSingleView, profiles]);

  if (practitionerSuggestions.length === 0) {
    return null;
  }

  const suggestions: SuggestionData[] = practitionerSuggestions;

  return (
    <CategoryView>
      {!showSingleView && <CategoryLabel>Practitioners</CategoryLabel>}
      <ListContainer onFocus={() => onFocus?.(listIndex)}>
        <SuggestionList
          suggestions={suggestions}
          onChoose={onChoose}
          trackingData={trackingData}
          searchTerm={searchTerm}
          toggleSingleSuggestionView={toggleSingleSuggestionView}
          showSingleView={showSingleView}
          suggestionType="Practitioners"
          indexIdentifier={listIndex}
        />
      </ListContainer>
    </CategoryView>
  );
};
